import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { isLocalizedRoute, localeFromPath, stripLocalePrefix, type Locale } from '@/i18n';
import { lerIdiomaSalvo } from '@/i18n/preferencia';

type Ctx = { locale: Locale };
const LocaleContext = createContext<Ctx>({ locale: 'pt-BR' });

/**
 * LocaleProvider — determina o idioma corrente pela URL e o coloca em contexto.
 * Também aplica changeLanguage sync no i18next para os hooks useTranslation
 * globais funcionarem no client.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Assina languageChanged: nas rotas sem prefixo o idioma vem do i18next, e sem esta
  // assinatura o contexto ficaria preso no valor do primeiro render.
  const { i18n: i18nInstance } = useTranslation();
  const urlLocale = localeFromPath(location.pathname);

  // Em /app/* e /login a URL não carrega idioma, então ela não pode mandar: forçar
  // o locale da URL aqui reverteria para pt-BR a cada render e o LanguageSwitcher
  // não teria efeito nenhum. Nessas rotas quem manda é a escolha do usuário,
  // guardada no próprio i18next.
  const urlManda = isLocalizedRoute(stripLocalePrefix(location.pathname));
  const locale = urlManda ? urlLocale : ((i18nInstance.language as Locale) ?? urlLocale);

  // Sync com i18next (para useTranslation() global e switching no client).
  // Em SSG isso muda o `i18n.language` mas os hooks já podem ter capturado,
  // por isso as páginas do site usam `useT()` que consulta este contexto.
  if (urlManda && i18n.language !== locale) {
    i18n.changeLanguage(locale);
  }

  // Entrando direto em /app ou /login (F5, link salvo), o i18next nasce em pt-BR: aplica a
  // escolha guardada no cookie. Só na montagem, para não desfazer uma troca feita depois.
  useEffect(() => {
    if (urlManda) return;
    const salvo = lerIdiomaSalvo();
    if (salvo && salvo !== i18n.language) i18n.changeLanguage(salvo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ locale }), [locale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Hook que retorna uma função `t` com o idioma FIXADO no locale corrente.
 * Ao contrário de useTranslation, funciona corretamente em SSG porque não
 * depende do estado interno do react-i18next.
 */
export function useT(namespace: string) {
  const { locale } = useContext(LocaleContext);
  // getFixedT retorna uma t com language+namespace fixados sync.
  return useMemo(() => i18n.getFixedT(locale, namespace), [locale, namespace]);
}

export function useLocale(): Locale {
  return useContext(LocaleContext).locale;
}
